import { useMemo } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import { useOwnerOptions } from '@/features/rocks/hooks/useRocks';

interface OwnerAvatarProps {
  /** Employee id (users.id UUID). Used to look up a photo via the shared employees cache. */
  ownerId?: string | null;
  /** MongoDB user _id. Alternative lookup key for MongoDB-backed features (QMS, legacy). */
  mongoUserId?: string | null;
  /**
   * Direct photo URL. Takes precedence over the ownerId / mongoUserId lookups — use
   * this when the row already carries profile_photo_url (e.g. the Employees tab).
   */
  photoUrl?: string | null;
  /** Display name used for alt text + initials fallback. */
  name: string | null | undefined;
  /** Tailwind size override (default h-6 w-6). */
  className?: string;
  /** Font size for the fallback initials. Default text-[10px]. */
  fallbackClassName?: string;
}

function getInitials(name: string): string {
  if (!name.trim()) return '?';
  return name
    .trim()
    .split(' ')
    .filter(Boolean)
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

/**
 * Owner avatar — shows the employee's profile photo if available, otherwise
 * initials. Pulls photos from the shared `/v4/employees` cache so every
 * consumer dedupes through the same TanStack Query.
 */
export function OwnerAvatar({
  ownerId,
  mongoUserId,
  photoUrl,
  name,
  className,
  fallbackClassName,
}: OwnerAvatarProps) {
  // Shared `/v4/employees` cache — TanStack Query dedupes across consumers.
  const { data: owners = [] } = useOwnerOptions();

  const photo = useMemo(() => {
    if (photoUrl) return photoUrl;
    if (ownerId) {
      return owners.find((o) => o.id === ownerId)?.profilePhotoUrl ?? null;
    }
    if (mongoUserId) {
      return owners.find((o) => o.mongoUserId === mongoUserId)?.profilePhotoUrl ?? null;
    }
    return null;
  }, [photoUrl, owners, ownerId, mongoUserId]);

  const safeName = name?.trim() || '?';

  return (
    <Avatar className={cn('h-6 w-6', className)}>
      {photo && <AvatarImage src={photo} alt={safeName} />}
      <AvatarFallback
        className={cn(
          'text-[10px] font-medium bg-muted text-muted-foreground',
          fallbackClassName,
        )}
      >
        {getInitials(safeName)}
      </AvatarFallback>
    </Avatar>
  );
}
